import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ShareLinksList = () => {
  const [shareLinks, setShareLinks] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newLink, setNewLink] = useState({
    passenger_id: '',
    flights_type: '9',
    name: ''
  });
  const [error, setError] = useState('');
  const [viewingVisits, setViewingVisits] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [editingFlightsType, setEditingFlightsType] = useState(null);
  const [openActionsMenu, setOpenActionsMenu] = useState(null);
  const [currentVisitsPage, setCurrentVisitsPage] = useState(1);
  const visitsPerPage = 20;

  useEffect(() => {
    fetchShareLinks();
    fetchPassengers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openActionsMenu && !e.target.closest('.actions-menu-container')) {
        setOpenActionsMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionsMenu]);

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && viewingVisits) {
        setViewingVisits(null);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [viewingVisits]);

  const fetchShareLinks = async () => {
    try {
      const response = await api.get('/share-links');
      setShareLinks(response.data);
    } catch (err) {
      setError('Failed to load share links');
      console.error(err);
    }
  };

  const fetchPassengers = async () => {
    try {
      const response = await api.get('/passengers');
      setPassengers(response.data);
    } catch (err) {
      console.error('Failed to load passengers:', err);
    }
  };

  const fetchVisits = async (linkId) => {
    try {
      setLoadingVisits(true);
      setCurrentVisitsPage(1);
      const response = await api.get(`/share-links/${linkId}/visits`);
      setVisits(response.data);
      setViewingVisits(linkId);
    } catch (err) {
      setError('Failed to load visit history');
      console.error(err);
    } finally {
      setLoadingVisits(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLink.passenger_id) {
      setError('Please select a passenger');
      return;
    }

    try {
      await api.post('/share-links', newLink);
      setNewLink({ passenger_id: '', flights_type: '9', name: '' });
      setIsCreating(false);
      setError('');
      fetchShareLinks();
    } catch (err) {
      setError('Failed to create share link');
      console.error(err);
    }
  };

  const handleToggleValidity = async (linkId, currentValidity) => {
    try {
      await api.patch(`/share-links/${linkId}`, {
        is_valid: currentValidity ? 0 : 1
      });
      fetchShareLinks();
    } catch (err) {
      setError('Failed to update share link');
      console.error(err);
    }
  };

  const handleUpdateFlightsType = async (linkId, newFlightsType) => {
    try {
      await api.patch(`/share-links/${linkId}`, {
        flights_type: newFlightsType
      });
      setEditingFlightsType(null);
      fetchShareLinks();
    } catch (err) {
      setError('Failed to update flights type');
      console.error(err);
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this share link?')) {
      return;
    }

    try {
      await api.delete(`/share-links/${linkId}`);
      fetchShareLinks();
    } catch (err) {
      setError('Failed to delete share link');
      console.error(err);
    }
  };

  const getShareUrl = (linkUidBase58) => {
    return `${window.location.origin}/share/${linkUidBase58}`;
  };

  const copyToClipboard = (text, linkId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedLinkId(linkId);
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 3000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const getFlightsTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Past';
      case 2: return 'Scheduled';
      case 9: return 'All';
      default: return 'Unknown';
    }
  };

  const getPassengerName = (passengerId) => {
    const passenger = passengers.find(p => p.passenger_id === passengerId);
    return passenger ? passenger.passenger_name : 'Unknown';
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);

    const parts = [];
    
    if (diffDays > 0) {
      parts.push(`${diffDays} d`);
      const remainingHrs = diffHr % 24;
      if (remainingHrs > 0) parts.push(`${remainingHrs} h`);
    } else if (diffHr > 0) {
      parts.push(`${diffHr} h`);
      const remainingMin = diffMin % 60;
      if (remainingMin > 0) parts.push(`${remainingMin} min`);
    } else if (diffMin > 0) {
      parts.push(`${diffMin} min`);
    } else {
      parts.push(`${diffSec} sec`);
    }

    return parts.join(' ') + ' ago';
  };

  const formatFullDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(',', '');
  };

  return (
    <div>
      <div className="page-header">
        <h2>Share Links</h2>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="btn btn-primary"
          >
            Create New Link
          </button>
        )}
      </div>

      {error && (
        <div className="error">
          {error}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        {isCreating && (
          <div className="form-container">
            <h3>Create New Share Link</h3>
            <form onSubmit={handleCreate}>
            
            <div className="form-group">
              <label>
                Link Name (optional):
              </label>
              <input
                type="text"
                value={newLink.name}
                onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                placeholder="e.g., My Flight History"
              />
            </div>

            <div className="form-group">
              <label>
                Passenger: *
              </label>
              <select
                value={newLink.passenger_id}
                onChange={(e) => setNewLink({ ...newLink, passenger_id: e.target.value })}
                required
              >
                <option value="">Select passenger</option>
                {passengers.map(p => (
                  <option key={p.passenger_id} value={p.passenger_id}>
                    {p.passenger_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                Flights Type:
              </label>
              <select
                value={newLink.flights_type}
                onChange={(e) => setNewLink({ ...newLink, flights_type: e.target.value })}
              >
                <option value="1">Past</option>
                <option value="2">Scheduled</option>
                <option value="9">All</option>
              </select>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-success"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCreating(false);
                  setNewLink({ passenger_id: '', flights_type: '9', name: '' });
                  setError('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
            </form>
          </div>
        )}
      </div>

      <div>
        {shareLinks.length === 0 ? (
          <div className="loading">No share links created yet.</div>
        ) : (
          <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Passenger</th>
                <th>Flights</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Visits</th>
                <th style={{ textAlign: 'center' }}>Last Visit</th>
                <th>Link</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shareLinks.map(link => (
                <tr key={link.slink_id}>
                  <td>
                    {link.slink_name || <em style={{ color: '#999' }}>Unnamed</em>}
                  </td>
                  <td>
                    {getPassengerName(link.slink_passenger_id)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {editingFlightsType === link.slink_id ? (
                        <select
                          value={link.slink_flights_type}
                          onChange={(e) => handleUpdateFlightsType(link.slink_id, e.target.value)}
                          onBlur={() => setEditingFlightsType(null)}
                          autoFocus
                          style={{ fontSize: '12px', padding: '4px' }}
                        >
                          <option value="1">Past</option>
                          <option value="2">Scheduled</option>
                          <option value="9">All</option>
                        </select>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingFlightsType(link.slink_id)}
                            style={{
                              background: 'none',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              padding: '4px 6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#666'
                            }}
                            title="Change flights type"
                          >
                            ⚙
                          </button>
                          <span>{getFlightsTypeLabel(link.slink_flights_type)}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: link.slink_is_valid ? '#e8f5e9' : '#ffebee',
                      color: link.slink_is_valid ? '#2e7d32' : '#c62828'
                    }}>
                      {link.slink_is_valid ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => fetchVisits(link.slink_id)}
                      className={link.visit_count > 0 ? "btn btn-primary" : "btn btn-secondary"}
                      disabled={link.visit_count === 0}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      {link.visit_count} {link.visit_count === 1 ? 'visit' : 'visits'}
                    </button>
                  </td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    {link.slink_last_visited ? (
                      <span title={formatFullDateTime(link.slink_last_visited)}>
                        {formatTimeAgo(link.slink_last_visited)}
                      </span>
                    ) : (
                      <em style={{ color: '#999' }}>Never</em>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={getShareUrl(link.slink_uid_base58)}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '6px',
                          fontSize: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#fafafa'
                        }}
                      />
                      <button
                        onClick={() => copyToClipboard(getShareUrl(link.slink_uid_base58), link.slink_id)}
                        className="btn btn-primary"
                        disabled={copiedLinkId === link.slink_id}
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                      >
                        {copiedLinkId === link.slink_id ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="actions actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={(e) => {
                          setOpenActionsMenu(openActionsMenu === link.slink_id ? null : link.slink_id);
                        }}
                        style={{
                          background: 'none',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#666'
                        }}
                        title="Actions"
                      >
                        ⋮
                      </button>
                      
                      {openActionsMenu === link.slink_id && (
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          bottom: '100%',
                          marginBottom: '4px',
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          minWidth: '120px'
                        }}>
                          <button
                            onClick={() => {
                              handleToggleValidity(link.slink_id, link.slink_is_valid);
                              setOpenActionsMenu(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: link.slink_is_valid ? '#666' : '#2e7d32',
                              borderBottom: '1px solid #eee'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            {link.slink_is_valid ? '✕ Disable' : '✓ Enable'}
                          </button>
                          <button
                            onClick={() => {
                              handleDelete(link.slink_id);
                              setOpenActionsMenu(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#c62828'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            🗑 Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Visits History Modal */}
      {viewingVisits && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setViewingVisits(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed Header */}
            <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>Visit History</h3>
                <button
                  onClick={() => setViewingVisits(null)}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
              {loadingVisits ? (
                <div className="loading">
                  Loading visits...
                </div>
              ) : visits.length === 0 ? (
                <div className="loading">
                  No visits recorded yet.
                </div>
              ) : (
                <>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Date & Time</th>
                          <th>IP Address</th>
                          <th>User Agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visits
                          .slice((currentVisitsPage - 1) * visitsPerPage, currentVisitsPage * visitsPerPage)
                          .map((visit) => (
                            <tr key={visit.slv_id}>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {formatFullDateTime(visit.slv_time)}
                              </td>
                              <td>
                                {visit.ip_value || 'Unknown'}
                              </td>
                              <td style={{ fontSize: '12px', color: '#666', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {visit.ua_value || 'Unknown'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {visits.length > visitsPerPage && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px',
                      marginTop: '20px',
                      padding: '10px 0'
                    }}>
                      <button
                        onClick={() => setCurrentVisitsPage(currentVisitsPage - 1)}
                        disabled={currentVisitsPage === 1}
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        Page {currentVisitsPage} of {Math.ceil(visits.length / visitsPerPage)}
                      </span>
                      <button
                        onClick={() => setCurrentVisitsPage(currentVisitsPage + 1)}
                        disabled={currentVisitsPage >= Math.ceil(visits.length / visitsPerPage)}
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareLinksList;
